import { useGbpListingQuery } from '../api/queries';
import type { GbpListing, GbpService } from '../types';

type Props = { af: string };

const MUTED = '#8a95a5';

export default function GbpListingPanel({ af }: Props) {
  const { data, isLoading, error } = useGbpListingQuery(af);

  if (isLoading) {
    return (
      <div className="ds-panel" style={{ padding: 20, margin: '16px 20px' }}>
        <div style={{ color: MUTED, fontSize: 13 }}>Loading GBP listing…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="ds-panel" style={{ padding: 20, margin: '16px 20px' }}>
        <div style={{ color: '#dc2626', fontSize: 13 }}>
          Failed to load GBP listing: {(error as Error).message}
        </div>
      </div>
    );
  }
  if (!data || !data.configured) {
    return (
      <div className="ds-panel" style={{ padding: 20, margin: '16px 20px' }}>
        <div style={{ color: MUTED, fontSize: 13 }}>
          The Google Business Profile connection isn't set up here yet.
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '8px 20px 20px' }}>
      <div className="ds-section-header">
        <h2>GBP Service Areas &amp; Services</h2>
        <span className="meta">
          {data.listings.length === 0
            ? 'No GBP listing has this AF as its store code'
            : `${data.listings.length} listing${data.listings.length === 1 ? '' : 's'} · ${data.zips.length} ZIP${data.zips.length === 1 ? '' : 's'} on the listing`}
        </span>
      </div>

      {data.inventoryPageCount === 0 && data.listings.length > 0 && (
        <div className="ds-panel" style={{ padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#b45309' }}>
          This site's pages haven't been loaded into the dashboard yet, so related pages
          can't be matched. They'll appear once marketing builds the site inventory.
        </div>
      )}

      {data.listings.map((l) => (
        <ListingBlock
          key={l.locationId}
          listing={l}
          showCode={data.listings.length > 1}
          hasInventory={data.inventoryPageCount > 0}
        />
      ))}

      <div className="metrics-footer">
        {data.source === 'fixture' ? 'Sample GBP data (dev)' : 'Live from the Google Business Profile API'}
        {' · '}Fetched {new Date(data.fetchedAt).toLocaleString()} · Google stores towns and
        counties; a ZIP shows only where the listing includes one.
      </div>
    </div>
  );
}

function ListingBlock({
  listing,
  showCode,
  hasInventory,
}: {
  listing: GbpListing;
  showCode: boolean;
  hasInventory: boolean;
}) {
  return (
    <div className="ds-panel" style={{ padding: 20, marginBottom: 16 }}>
      {showCode && (
        <div style={{ fontWeight: 600, color: '#1a3a5c', marginBottom: 12 }}>
          {listing.storeCode} · {listing.title}
        </div>
      )}

      <div style={{ fontSize: 12, fontWeight: 600, color: MUTED, textTransform: 'uppercase', marginBottom: 8 }}>
        Service areas ({listing.serviceAreas.length} of 20)
      </div>
      {listing.serviceAreas.length === 0 ? (
        <div style={{ color: MUTED, fontSize: 13, marginBottom: 16 }}>No service areas set on this listing.</div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
          {listing.serviceAreas.map((a) => (
            <span
              key={`${a.name}-${a.zip ?? ''}`}
              style={{
                padding: '4px 10px',
                borderRadius: 14,
                border: '1px solid #d0d5dd',
                background: a.isCounty ? '#f3f6f9' : '#fff',
                fontSize: 12,
                color: '#1a3a5c',
              }}
            >
              {a.name}
              {a.zip && <strong style={{ marginLeft: 6 }}>{a.zip}</strong>}
            </span>
          ))}
        </div>
      )}

      <div style={{ fontSize: 12, fontWeight: 600, color: MUTED, textTransform: 'uppercase', marginBottom: 8 }}>
        Services ({listing.services.length})
      </div>
      {listing.services.length === 0 ? (
        <div style={{ color: MUTED, fontSize: 13 }}>No services set on this listing.</div>
      ) : (
        <table className="ds-table">
          <thead>
            <tr>
              <th>Service on GBP</th>
              <th>Related page on the site</th>
              <th className="num">City pages</th>
            </tr>
          </thead>
          <tbody>
            {listing.services.map((s, i) => (
              <ServiceRow key={`${s.label}-${i}`} service={s} hasInventory={hasInventory} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ServiceRow({ service, hasInventory }: { service: GbpService; hasInventory: boolean }) {
  return (
    <tr>
      <td>
        <div className="ds-name">{service.label}</div>
        {service.description && <div className="ds-sub">{service.description}</div>}
      </td>
      <td>
        {!hasInventory ? (
          <span style={{ color: MUTED }}>—</span>
        ) : service.relatedPages.length === 0 ? (
          <span style={{ color: '#b45309', fontSize: 13 }}>No page on the site</span>
        ) : (
          service.relatedPages.map((p) => (
            <div key={p.url}>
              <a href={p.url} target="_blank" rel="noopener noreferrer">
                {p.path}
              </a>
            </div>
          ))
        )}
      </td>
      <td className="num">{service.cityPageCount || '—'}</td>
    </tr>
  );
}
